// Define the structure for the initially parsed topic data
// including categoryId initialized to null, but allowing string later.
export interface InitialTopicData {
  topicName: string;
  description: string;
  categoryName: string;
  categoryId: string | null; // Allow string or null
}

// Helper function to parse a single CSV line, handling quoted fields robustly
export const parseCsvLine = (line: string): string[] => {
  // Regex to split by comma, but ignore commas inside double quotes
  // Handles escaped quotes ("") inside quoted fields.
  const regex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
  const columns = line.split(regex);

  return columns.map((part) => {
    // Trim whitespace from the part
    let cleanedPart = part.trim();

    // Check if the part is quoted
    if (cleanedPart.startsWith('"') && cleanedPart.endsWith('"')) {
      // Remove the surrounding quotes
      cleanedPart = cleanedPart.substring(1, cleanedPart.length - 1);
      // Replace escaped double quotes ("") with a single double quote (")
      cleanedPart = cleanedPart.replace(/""/g, '"');
    }
    // No need to trim again as surrounding quotes are removed after initial trim

    return cleanedPart;
  });
};

// --- Combined Parsing and Validation Function --- //

/**
 * Parses CSV text and validates it according to specific rules.
 * - Expects 3 columns: Topic Name, Description, Category Name.
 * - Requires all three columns to be non-empty after parsing.
 * - Handles an optional header row.
 * - Collects all errors found during processing.
 *
 * @param textContent The CSV string content.
 * @returns An object containing the successfully parsed data and any validation errors.
 */
export const parseAndValidateCsvText = (
  textContent: string | undefined
): { data: InitialTopicData[]; errors: string[] } => {
  const parsedTopics: InitialTopicData[] = [];
  const errors: string[] = [];

  if (!textContent) {
    return { data: [], errors: [] }; // Return empty if no content
  }

  let lines = textContent.trim().split("\n");
  let headerSkipped = false;

  // Check for and remove the header row, ignoring whitespace variations
  if (lines.length > 0) {
    const firstLineTrimmed = lines[0].trim();
    // Use the parser for the header check
    const headerParts = parseCsvLine(firstLineTrimmed);
    if (
      headerParts.length === 3 &&
      headerParts[0].toLowerCase() === "topic name" && // Case-insensitive check
      headerParts[1].toLowerCase() === "topic description" &&
      headerParts[2].toLowerCase() === "category name"
    ) {
      lines = lines.slice(1); // Skip header line
      headerSkipped = true;
    }
  }

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return; // Skip empty lines silently

    // Calculate the original line number for error messages
    const originalLineNumber = index + 1 + (headerSkipped ? 1 : 0);

    const columns = parseCsvLine(trimmedLine);

    let lineHasError = false;

    // Validation 1: Must have exactly 3 columns
    if (columns.length !== 3) {
      errors.push(
        `Line ${originalLineNumber}: Expected 3 columns, found ${columns.length}. Content: "${line}"`
      );
      lineHasError = true;
    } else {
      // Validation 2: All columns must be non-empty
      const emptyColumnIndex = columns.findIndex((col) => !col);
      if (emptyColumnIndex !== -1) {
        const columnName = ["Topic Name", "Description", "Category Name"][
          emptyColumnIndex
        ];
        errors.push(
          `Line ${originalLineNumber}: Column "${columnName}" cannot be empty. Content: "${line}"`
        );
        lineHasError = true;
      }
    }

    // If no errors on this line, add to parsed data
    if (!lineHasError && columns.length === 3) {
      // Added columns.length check again for type safety after validation
      parsedTopics.push({
        topicName: columns[0],
        description: columns[1],
        categoryName: columns[2],
        categoryId: null, // Initialize as null
      });
    }
  });

  return { data: parsedTopics, errors: errors };
};

// --- Removed old parseCsvText and validateCsvTextFormat --- //
// --- Removed ParsedCsvRow interface as it's no longer used --- //
